import { DOCUMENT, PAY_FREQUENCY } from '@config/constants';

const filterByTypeAndGroup = ({
  documents,
  type,
  group,
}) => documents
  .find(({
    documentType,
    documentGrouping,
  }) => [
    documentGrouping === group,
    documentType === type,
  ].every(Boolean));

const count = (arr) => arr.reduce((acc, doc) => (doc ? acc + 1 : acc), 0);

const parsePayload = ({
  uploadedFiles,
  requiredCount,
}) => ({
  uploadedCount: count(uploadedFiles),
  missingCount: requiredCount - count(uploadedFiles),
  isCompleted: count(uploadedFiles) === requiredCount,
  isTouched: count(uploadedFiles) > 0,
});

const getIDCardMissingDocuments = ({ documents }) => {
  const idCardFront = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.ID_CARD,
    group: DOCUMENT.GROUPS.FRONT,
  });
  const idCardBack = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.ID_CARD,
    group: DOCUMENT.GROUPS.REAR,
  });
  return parsePayload({
    uploadedFiles: [idCardFront, idCardBack],
    requiredCount: 2,
  });
};

const getPassportMissingDocuments = ({ documents }) => {
  const passport = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PASSPORT,
    group: DOCUMENT.GROUPS.PASSPORT,
  });
  return parsePayload({
    uploadedFiles: [passport],
    requiredCount: 1,
  });
};

const getProofOfAddressMissingDocuments = ({ documents }) => {
  const proofOfAddress = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PROOF_OF_ADDRESS,
    group: DOCUMENT.GROUPS.ADDRESS,
  });
  return parsePayload({
    uploadedFiles: [proofOfAddress],
    requiredCount: 1,
  });
};

const getBankStatementMissingDocuments = ({ documents }) => {
  const lastPeriod = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.BANK_STATEMENT,
    group: DOCUMENT.GROUPS.LAST_PERIOD,
  });
  const onePeriodAgo = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.BANK_STATEMENT,
    group: DOCUMENT.GROUPS.ONE_PERIOD_AGO,
  });
  return parsePayload({
    uploadedFiles: [lastPeriod, onePeriodAgo],
    requiredCount: 2,
  });
};

const getMissingPayslipDocuments = ({ documents, paymentFrequency }) => {
  const lastPeriod = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.LAST_PERIOD,
  });
  const onePeriodAgo = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.ONE_PERIOD_AGO,
  });
  if (paymentFrequency === PAY_FREQUENCY.MONTHLY) {
    return parsePayload({
      uploadedFiles: [lastPeriod, onePeriodAgo],
      requiredCount: 2,
    });
  }
  const twoPeriodsAgo = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.TWO_PERIODS_AGO,
  });
  const threePeriodsAgo = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.PAYSLIP,
    group: DOCUMENT.GROUPS.THREE_PERIODS_AGO,
  });
  return parsePayload({
    uploadedFiles: [lastPeriod, onePeriodAgo, twoPeriodsAgo, threePeriodsAgo],
    requiredCount: 4,
  });
};

const getMissingTaxSituationDocuments = ({ documents }) => {
  const taxSituation = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.TAX_SITUATION,
    group: DOCUMENT.GROUPS.TAX_SITUATION,
  });
  return parsePayload({
    uploadedFiles: [taxSituation],
    requiredCount: 1,
  });
};

const getMissingOperatingLicenseDocuments = ({ documents }) => {
  const operatingLicense = filterByTypeAndGroup({
    documents,
    type: DOCUMENT.TYPE.OPERATING_LICENSE,
    group: DOCUMENT.GROUPS.OPERATING_LICENSE,
  });
  return parsePayload({
    uploadedFiles: [operatingLicense],
    requiredCount: 1,
  });
};

export const getMissingDocumentsByType = ({
  type,
  documents,
  paymentFrequency,
}) => {
  switch (type) {
    case DOCUMENT.TYPE.ID_CARD:
      return getIDCardMissingDocuments({ documents });
    case DOCUMENT.TYPE.PASSPORT:
      return getPassportMissingDocuments({ documents });
    case DOCUMENT.TYPE.PROOF_OF_ADDRESS:
      return getProofOfAddressMissingDocuments({ documents });
    case DOCUMENT.TYPE.BANK_STATEMENT:
      return getBankStatementMissingDocuments({ documents, paymentFrequency });
    case DOCUMENT.TYPE.PAYSLIP:
      return getMissingPayslipDocuments({ documents, paymentFrequency });
    case DOCUMENT.TYPE.TAX_SITUATION:
      return getMissingTaxSituationDocuments({ documents });
    case DOCUMENT.TYPE.OPERATING_LICENSE:
      return getMissingOperatingLicenseDocuments({ documents });
    default: return null;
  }
};

const MISSING_DOCUMENTS_PLACEHOLDER = {
  missingCount: 0,
  uploadedCount: 0,
  isCompleted: true,
};
const getMissingIDCardDocument = ({
  documents,
}) => {
  const OfficialID = getIDCardMissingDocuments({ documents });
  const passport = getPassportMissingDocuments({ documents });
  if (OfficialID.isTouched) return OfficialID;
  if (passport.isTouched) return passport;
  if (!OfficialID.isCompleted) return OfficialID;
  return passport;
};

const getMissingProofOfAddressDocument = ({
  documents,
}) => getProofOfAddressMissingDocuments({ documents });

const getProofOfIncomeMissingDocuments = ({
  documents,
  paymentFrequency,
}) => {
  const payslip = getMissingPayslipDocuments({ documents, paymentFrequency });
  const bankStatement = getBankStatementMissingDocuments({ documents, paymentFrequency });
  if (bankStatement.isTouched) return bankStatement;
  if (payslip.isTouched) return payslip;
  if (!bankStatement.isCompleted) return bankStatement;
  return payslip;
};

const getMissingProofOfActivityDocument = ({
  documents,
}) => {
  const taxSituation = getMissingTaxSituationDocuments({ documents });
  const operatingLicense = getMissingOperatingLicenseDocuments({ documents });
  if (taxSituation.isCompleted) return taxSituation;
  if (operatingLicense.isCompleted) return operatingLicense;
  if (!taxSituation.isCompleted) return taxSituation;
  return operatingLicense;
};

export const getMissingDocumentsByKlass = ({
  klass,
  documents,
  paymentFrequency,
}) => {
  if (!klass) return MISSING_DOCUMENTS_PLACEHOLDER;
  const byKlass = {
    [DOCUMENT.CLASS.ID_CARD]: getMissingIDCardDocument,
    [DOCUMENT.CLASS.PROOF_OF_ADDRESS]: getMissingProofOfAddressDocument,
    [DOCUMENT.CLASS.PROOF_OF_INCOME]: getProofOfIncomeMissingDocuments,
    [DOCUMENT.CLASS.PROOF_OF_ACTIVITY]: getMissingProofOfActivityDocument,
  };

  return byKlass[klass]({ documents, paymentFrequency });
};
